import { useState, useEffect, useContext } from 'react';
import { Grid, Box, InputAdornment, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttDialog,
    SttFormCheck,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttSelectPForm,
    SttSelectUser,
    SttTabs,
} from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import { fullName, generateFormField } from '../../../lib/utils';
import SttMshipDialog from '../../../sporttia/dialogs/SttMshipDialog';
import type { User } from '../../../types/models/User';
import type { Mship } from '../../../types/models/Mship';
import { PERMISSIONS } from '../../../lib/permissions';

type FormAnswer = {
    value: unknown;
};

type FormField = {
    id: string | number;
    name?: string;
    type?: string;
    answer?: FormAnswer;
    options?: Array<{
        name: string;
        value: string;
    }>;
};

type Team = {
    id: string | number;
    name?: string;
};

type Creator = {
    id?: string | number;
    name?: string;
    surname1?: string;
    surname2?: string;
    displayName?: string;
};

type Payment = {
    id?: string | number;
    paymentForm?: string;
    price?: number;
};

type Participant = {
    id?: string | number;
    name?: string;
    email?: string;
    mobile?: string;
    notes?: string;
    displayName?: string;
    user?: User;
    creator?: Creator;
    team?: Team;
    admin?: boolean;
    paymentForm?: string;
    form?: FormField[];
    enrollment?: Payment;
};

type Category = {
    id: string | number;
    name: string;
    enrollment?: number | string;
    enrollmentShape?: {
        currency?: string;
    };
};

type Event = {
    id: string | number;
    name: string;
    teamable?: boolean;
    form?: FormField[];
};

type Props = {
    item: Participant;
    event: Event;
    category: Category;
    teams?: Team[];
    onClose: () => void;
    onSave: (participant: Participant) => void | Promise<unknown>;
    onDelete: (participant: Participant) => void;
    disableFieldsForParticipantWithUser?: boolean;
};

export default function ModalParticipant({
    item,
    event,
    category,
    teams,
    onClose,
    onSave,
    onDelete,
    disableFieldsForParticipantWithUser = false,
}: Props): React.ReactElement {
    const [participant, setParticipant, setProperty] =
        useCrud<Participant>(item);
    const [participantCreator, setParticipantCreator] = useState<Mship | null>(
        null,
    );
    const [openCreator, setOpenCreator] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    async function handleSaveClick(): Promise<void> {
        if (saving) return;
        setSaving(true);
        try {
            const result = onSave(participant);
            if (
                result &&
                typeof (result as Promise<unknown>).then === 'function'
            ) {
                await result;
            }
        } finally {
            setSaving(false);
        }
    }

    function handleFormFieldChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        const { form } = participant;
        if (!form) return;
        const question = form.find((q: any) => q.id === Number(name));

        if (question) {
            form.splice(form.indexOf(question), 1, {
                ...question,
                answer: { value },
            });
            setParticipant({ ...participant, form });
        }
    }

    const getCreatorData = (): void => {
        const creatorId = participant.creator?.id;
        if (!creatorId) return;

        cxt?.api('GET', `/users/${creatorId}`, {
            params: {},
            version: 'v7',
            success: (result: {
                user?: { mship?: { id: string | number } };
            }) => {
                const mshipId = result.user?.mship?.id;
                if (mshipId) {
                    cxt.api('GET', `/mships/${mshipId}`, {
                        params: {},
                        version: 'v7',
                        success: (response: { mship?: Mship }) => {
                            if (response.mship) {
                                setParticipantCreator(response.mship);
                            }
                        },
                    });
                }
            },
        });
    };

    const participantData = (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
                {participant && participant.user ? (
                    <SttSelectUser
                        user={participant.user as any}
                        caption={t('User')}
                        onSelect={(mship?: any) => {
                            if (mship && 'user' in mship) {
                                setProperty({
                                    name: 'user',
                                    value: {
                                        fullName: mship.fullName,
                                        id: mship.user?.id,
                                    } as unknown as User,
                                });
                            } else {
                                setProperty({
                                    name: 'user',
                                    value: {
                                        fullName: ' ',
                                    } as unknown as User,
                                });
                            }
                        }}
                        returnMship
                    />
                ) : (
                    <SttFormText
                        caption={t('User')}
                        name="user"
                        defVal={participant.displayName}
                        disabled
                    />
                )}
                {participant && participant.id && participantCreator && (
                    <Box pt={1}>
                        <b>{t('RegisteredBy')}: </b>
                        <Link
                            onClick={() => setOpenCreator(true)}
                            sx={{
                                color: 'var(--brand-text)',
                                textDecoration: 'none',
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {fullName(participantCreator as any)}
                        </Link>
                    </Box>
                )}
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
                <SttFormText
                    caption={t('Email')}
                    name="email"
                    defVal={participant.email}
                    onChange={({ name, value }) =>
                        setProperty({
                            name: name as keyof Participant,
                            value: value as string,
                        })
                    }
                    disabled={
                        !!participant?.user &&
                        disableFieldsForParticipantWithUser
                    }
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
                <SttFormText
                    caption={t('Mobile')}
                    name="mobile"
                    defVal={participant.mobile}
                    onChange={({ name, value }) =>
                        setProperty({
                            name: name as keyof Participant,
                            value: value as string,
                        })
                    }
                    disabled={
                        !!participant?.user &&
                        disableFieldsForParticipantWithUser
                    }
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <SttFormTextarea
                    caption={t('Notes')}
                    name="notes"
                    defVal={participant.notes}
                    onChange={({ name, value }) =>
                        setProperty({
                            name: name as keyof Participant,
                            value: value as string,
                        })
                    }
                />
            </Grid>
            {event.teamable && teams ? (
                <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SttFormSelect
                            caption={t('Team')}
                            name="team"
                            defVal={
                                participant.team ? participant.team.id : '0'
                            }
                            options={[
                                {
                                    caption: t('CreateNewTeam'),
                                    value: '0',
                                },
                            ].concat(
                                teams.map((team: any) => ({
                                    caption: team.name || '',
                                    value: team.id,
                                })),
                            )}
                            onChange={({ value }) =>
                                setProperty({
                                    name: 'team',
                                    value: {
                                        id: value,
                                        name:
                                            value !== '0'
                                                ? teams.find(
                                                      (team: any) =>
                                                          team.id === value,
                                                  )?.name
                                                : t('CreateNewTeam'),
                                    } as Team,
                                })
                            }
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        {(!participant.team || participant.team.id === '0') && (
                            <SttFormText
                                caption={t('NewTeam')}
                                defVal={participant.team?.name}
                                onChange={({ value }) => {
                                    setProperty({
                                        name: 'team',
                                        value: { id: '0', name: value } as Team,
                                    });
                                }}
                            />
                        )}
                    </Grid>
                    <Grid
                        style={{ paddingTop: 0, paddingBottom: 0 }}
                        size={{ xs: 12 }}
                    >
                        <SttFormCheck
                            caption={t('Administrator')}
                            checked={participant.admin}
                            name="admin"
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Participant,
                                    value: value as boolean,
                                })
                            }
                        />
                    </Grid>
                </>
            ) : null}

            {category.enrollment &&
            (!event.teamable ||
                (event.teamable &&
                    participant.team &&
                    parseInt(participant.team.id as string) === 0)) ? (
                <>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <SttFormText
                            caption={t('generic.price')}
                            disabled
                            defVal={String(category.enrollment)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {category.enrollmentShape?.currency}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 9 }}>
                        <SttSelectPForm
                            disabled={!!participant.id}
                            name="paymentForm"
                            defVal={
                                participant.paymentForm ||
                                (participant.enrollment
                                    ? participant.enrollment.paymentForm
                                    : '')
                            }
                            activePFs={[
                                '',
                                'PURSE',
                                'CASH',
                                'BANK',
                                'DATAFONO',
                                'RECEIPT',
                                'FREE',
                            ]}
                            onChange={({ name, value }) =>
                                setProperty({
                                    name: name as keyof Participant,
                                    value: value as string,
                                })
                            }
                        />
                    </Grid>
                </>
            ) : null}
        </Grid>
    );

    const form = (
        <Grid container spacing={3}>
            {participant.form?.map((f: any) => {
                const eventFormItem = event.form?.find(
                    (itm: any) => itm.id === f.id,
                );
                if (!eventFormItem) return null;

                return (
                    <Grid key={f.id} size={{ xs: 12 }}>
                        {generateFormField(
                            {
                                id: Number(eventFormItem.id),
                                name: eventFormItem.name || '',
                                type: eventFormItem.type || '',
                                answer: f.answer,
                                options: eventFormItem.options,
                            } as any,
                            handleFormFieldChange,
                        )}
                    </Grid>
                );
            })}
        </Grid>
    );

    useEffect(() => {
        if (participant.creator) {
            getCreatorData();
        }
    }, []);

    return (
        <>
            <SttDialog
                open
                title={t('generic.participant')}
                subtitle={`${event.name} - ${category.name}`}
                onClose={onClose}
                maxWidth="sm"
                content={
                    event.form ? (
                        <SttTabs
                            tabs={[
                                {
                                    caption: t('generic.participant'),
                                    component: participantData,
                                },
                                {
                                    caption: t('generic.form'),
                                    show: (event.form?.length || 0) > 0,
                                    component: form,
                                },
                            ]}
                        />
                    ) : (
                        participantData
                    )
                }
                buttons={
                    [
                        {
                            type: 'save',
                            onClick: handleSaveClick,
                            permission: PERMISSIONS.EVENT.MODIFY,
                            loading: saving,
                            disabled: saving,
                        },
                        participant.id
                            ? {
                                  type: 'delete',
                                  onClick: () => onDelete(participant),
                                  permission: PERMISSIONS.EVENT.MODIFY,
                                  disabled: saving,
                              }
                            : false,
                        {
                            type: 'cancel',
                            onClick: onClose,
                            disabled: saving,
                        },
                    ] as const
                }
            />

            <SttMshipDialog
                open={openCreator}
                mshipId={participantCreator?.id ?? null}
                onClose={() => {
                    setOpenCreator(false);
                }}
            />
        </>
    );
}
